import {
  ACCOUNT_FEATURE,
  normalizeAccountRole,
  ROLE_LIMIT,
  selectAvailableUserRoles,
  USER_ROLE,
} from '@socialive/common';
import { selectFeature } from '@socialive/features';
import PropTypes from 'prop-types';
import { Component } from 'react';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import UserRoleSelector from '~/components/UserRoleSelector';
import { MIN_PASSWORD_REQUIREMENTS_TEXT } from '~/helpers/constants';
import decodeURIComponentSafe from '~/helpers/decodeURIComponentSafe';
import { routerHistoryShape, routerLocationShape, routerMatchShape } from '~/helpers/propTypeShapes';
import { selectRolesLicensingData } from '~/store';
import { getRolesLicensing } from '~/store/accountInfo';
import '~/style/modal.css';
import '~/style/user.css';

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    width: '400px',
    padding: '0',
    transform: 'translate(-50%, -50%)',
  },
  overlay: {
    backgroundColor: 'rgb(0,0,0,0.5)',
  },
};

class User extends Component {
  constructor(props) {
    super(props);
    this.state = {
      email: '',
      immutableEmail: '',
      name: '',
      location: '',
      role: 'GUEST',
      userPassword: '',
      userPasswordConfirm: '',
      userFormIsValid: false,
      invalidFormMessage: '',
      isComponentReady: false,
      isNewUser: this.props.match.params.userId === 'new',
      userIsSelf: this.props.match.params.userId === sessionStorage.getItem('userId'),
      passwordRadio: false,
    };
    this.checkFormValidity = this.checkFormValidity.bind(this);
    this.handleUserEdit = this.handleUserEdit.bind(this);
    this.handleUserInvite = this.handleUserInvite.bind(this);
    this.handleUserSave = this.handleUserSave.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleRadioInputChange = this.handleRadioInputChange.bind(this);
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
  }

  componentDidMount() {
    if (!this.state.isNewUser) {
      const params = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-VERSION': '2.0',
          Authorization: sessionStorage.getItem('authToken'),
        },
      };

      fetch(`${this.props.APIBaseURL}/account/${this.props.accountId}/user/${this.props.match.params.userId}`, params)
        .then((response) => {
          if (response.status === 401) {
            // eslint-disable-next-line no-console
            console.error('Received 401 response from user fetch. Redirecting to login.');
            this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
            return undefined;
          }
          if (response.status === 403) {
            // eslint-disable-next-line no-console
            console.error('Received 403 response from user fetch.');
            toast.error('You do not have permission to perform this action.');
            return undefined;
          }
          return response.json();
        })
        .then((data) => {
          if (data && data.user && data.user.email) {
            const decodedRole = decodeURIComponentSafe(data.role ?? USER_ROLE.GUEST);
            const parsedRole = normalizeAccountRole(decodedRole);

            this.setState(
              {
                email: decodeURIComponentSafe(data.user.email),
                immutableEmail: decodeURIComponentSafe(data.user.email),
                name: decodeURIComponentSafe(data.user.name ?? ''),
                location: decodeURIComponentSafe(data.user.location ?? ''),
                role: parsedRole,
                isComponentReady: true,
              },
              () => {
                this.checkFormValidity();
              }
            );
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error editing user. ', error);
        });
    } else {
      this.setState({
        isComponentReady: true,
      });
    }
  }

  handleCancel() {
    this.props.history.push('/users');
  }

  handleEnterKey(event) {
    if (event.which === 13) {
      event.preventDefault();
      document.querySelector('#userSaveBtn').click();
    }
  }

  handleInputChange(event) {
    const { target } = event;
    const { value, name } = target;

    this.setState({ [name]: value }, () => {
      this.checkFormValidity();
    });
  }

  handleRadioInputChange(event) {
    const { target } = event;
    const value = target.value === 'true';

    this.setState({ passwordRadio: value }, () => {
      this.checkFormValidity();
    });
  }

  handleUserEdit() {
    if (this.state.userFormIsValid) {
      const params = {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-API-VERSION': '2.0',
          Authorization: sessionStorage.getItem('authToken'),
        },
        body: JSON.stringify({
          accountId: this.props.accountId,
          email: this.state.immutableEmail.trim(),
          location: this.state.location.trim(),
          name: this.state.name.trim(),
          role: this.state.role,
        }),
      };
      fetch(`${this.props.APIBaseURL}/user`, params)
        .then((response) => {
          if (response.status === 401) {
            // eslint-disable-next-line no-console
            console.error('Received 401 response from handleUserEdit. Redirecting to login.');
            this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
            return undefined;
          }
          if (response.status === 403) {
            // eslint-disable-next-line no-console
            console.error('Users may not change their own role.');
            toast.error('Users may not change their own role.');
            return undefined;
          }
          return response.json();
        })
        .then(() => {
          if (this.props.isUserRolesEnabled) {
            this.props.getRolesLicensing({ trigger: 'User@handleUserEdit' });
          }
          toast.success('User updated');
          this.props.history.push('/users');
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error editing user. ', error);
        });
    }
  }

  handleUserInvite() {
    if (this.state.userFormIsValid) {
      const params = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-VERSION': '2.0',
          Authorization: sessionStorage.getItem('authToken'),
        },
        body: JSON.stringify({
          account: { id: this.props.accountId },
          email: this.state.email.trim(),
          aliasName: this.state.location.trim(),
          name: this.state.name.trim(),
          role: this.state.role,
        }),
      };

      fetch(`${this.props.APIBaseURL}/invite/to-account`, params)
        .then((response) => {
          if (response.status === 401) {
            // eslint-disable-next-line no-console
            console.error('Received 401 response from handleUserInvite. Redirecting to login.');
            this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
          }
          if (response.status === 403) {
            // eslint-disable-next-line no-console
            console.error('Received 403 response from user fetch.');
            toast.error('You do not have permission to perform this action.');
          }
          if (response.status === 200 || response.status === 201) {
            toast.success('User invited');
            this.props.history.push('/users');
          }
        })
        .catch((error) => {
          toast.error('Unable to invite user. Please confirm they are not already an account member.');
          // eslint-disable-next-line no-console
          console.error('Error inviting user. ', error);
        });
    }
  }

  handleUserSave() {
    if (this.state.userFormIsValid) {
      const params = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-VERSION': '2.0',
          Authorization: sessionStorage.getItem('authToken'),
        },
        body: JSON.stringify({
          accountId: this.props.accountId,
          email: this.state.email.trim(),
          location: this.state.location.trim(),
          name: this.state.name.trim(),
          password: this.state.userPassword.trim(),
          role: this.state.role,
        }),
      };
      fetch(`${this.props.APIBaseURL}/user`, params)
        .then((response) => {
          function chainError(error) {
            throw new Error(error);
          }

          if (response.status === 400) {
            this.props.trackEvent('400', {
              source: 'handleUserSave',
            });
            response
              .text()
              .then((errorMessage) => {
                toast.error(errorMessage, { autoClose: 30000 });
                chainError(errorMessage);
              })
              .catch((error) => {
                // eslint-disable-next-line no-console
                console.error('Error inviting user. ', error);
              });
            return undefined;
          }
          if (response.status === 401) {
            // eslint-disable-next-line no-console
            console.error('Received 401 response from handleUserEdit. Redirecting to login.');
            this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
            return undefined;
          }
          if (response.status === 403) {
            // eslint-disable-next-line no-console
            console.error('Received 403 response from user fetch.');
            toast.error('You do not have permission to perform this action.');
            return undefined;
          }
          return response.json();
        })
        .then((data) => {
          if (data) {
            if (data.userCreated) {
              if (this.props.isUserRolesEnabled) {
                this.props.getRolesLicensing({ trigger: 'User@handleUserSave' });
              }
              toast.success('User created');
              this.props.history.push('/users');
            } else {
              // toast.success('A user with this email already exists. They have been added to your account.');
              this.openModal();
            }
          }
        })
        .catch((error) => {
          toast.error('Unable to create or add user. Please confirm they are not already an account member.');
          // eslint-disable-next-line no-console
          console.error('Error inviting user. ', error);
        });
    }
  }

  checkFormValidity() {
    const { isUserRolesEnabled, rolesLicense } = this.props;
    const targetRole = rolesLicense?.[this.state.role];

    const re = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/i;
    const isValidEmail = re.test(this.state.email);
    let isFormValid;
    let invalidFormMessage = '';

    if (this.state.isNewUser && !isValidEmail) {
      isFormValid = false;
      invalidFormMessage = 'Users must have a valid email address.';
    } else if (
      this.state.isNewUser &&
      this.state.passwordRadio &&
      this.state.userPassword.length > 0 &&
      this.state.userPassword !== this.state.userPasswordConfirm
    ) {
      isFormValid = false;
      invalidFormMessage = 'Passwords must match.';
    } else if (this.state.isNewUser && this.state.passwordRadio && this.state.userPassword.length === 0) {
      isFormValid = false;
      invalidFormMessage = 'Password is required.';
    } else if (this.state.isNewUser && this.state.passwordRadio && this.state.userPassword.length < 8) {
      isFormValid = false;
      invalidFormMessage = 'Password must contain at least 8 characters.';
    } else if (this.state.name.length === 0) {
      isFormValid = false;
      invalidFormMessage = 'Users must have a name.';
    } else if (
      isUserRolesEnabled &&
      targetRole?.limit !== ROLE_LIMIT.UNLIMITED &&
      targetRole?.current >= targetRole?.limit
    ) {
      invalidFormMessage = `You have reached the license limit for the ${this.state.role} role`;
      isFormValid = false;
    } else {
      isFormValid = true;
    }

    this.setState({
      userFormIsValid: isFormValid,
      invalidFormMessage,
    });
  }

  openModal() {
    this.setState({ modalIsOpen: true });
  }

  closeModal() {
    this.setState({ modalIsOpen: false });
    this.props.history.push('/users');
  }

  render() {
    const { availableRoles } = this.props;
    const { isNewUser, passwordRadio, userIsSelf, role } = this.state;

    let buttonAction = isNewUser ? this.handleUserSave : this.handleUserEdit;
    if (isNewUser && !passwordRadio) {
      buttonAction = this.handleUserInvite;
    }

    if (this.state.isComponentReady) {
      return (
        <div className="user-content content-container">
          <div className="user-header header-container">
            <h2 className="user-header-title">User profile</h2>
            <div className="user-header-buttons">
              <button
                type="button"
                id="cancelUserEdit"
                className="btn grey btn-user-cancel"
                onClick={this.handleCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                id="userSaveBtn"
                className="btn btn-user-save"
                disabled={!this.state.userFormIsValid}
                onClick={buttonAction}
              >
                Save
              </button>
              <div className={`error-msg ${this.state.userFormIsValid ? '' : 'active'}`}>
                {this.state.invalidFormMessage}
              </div>
            </div>
          </div>
          <div className="user-form-container">
            <form id="editUserForm" className="user-edit-form alt-form">
              <label className="form-input-label" htmlFor="email">
                EMAIL {!isNewUser && '(Cannot be changed)'}
              </label>
              <input
                className="form-input"
                id="email"
                name="email"
                onBlur={this.handleInputChange}
                onChange={this.handleInputChange}
                onKeyDown={this.handleEnterKey}
                type="email"
                value={this.state.email}
                readOnly={!isNewUser}
                autoComplete="off"
              />
              {isNewUser && (
                <fieldset className="form-radio-group set-password">
                  <div className="form-input-label password-label">PASSWORD</div>
                  <label className="form-radio-label" htmlFor="passwordRadio-1">
                    <input
                      id="passwordRadio-1"
                      className="form-input form-radio-button"
                      checked={this.state.passwordRadio === true}
                      name="passwordRadio"
                      onChange={this.handleRadioInputChange}
                      type="radio"
                      value="true"
                    />
                    Set password
                  </label>
                  <label className="form-radio-label" htmlFor="passwordRadio-2">
                    <input
                      id="passwordRadio-2"
                      className="form-input form-radio-button"
                      checked={this.state.passwordRadio === false}
                      name="passwordRadio"
                      onChange={this.handleRadioInputChange}
                      type="radio"
                      value="false"
                    />
                    Ask user to set password
                  </label>
                </fieldset>
              )}

              {isNewUser && passwordRadio && (
                <fieldset className="form-input-group password-inputs">
                  <input
                    className="form-input"
                    id="userPassword"
                    name="userPassword"
                    type="password"
                    onBlur={this.handleInputChange}
                    onChange={this.handleInputChange}
                    onKeyDown={this.handleEnterKey}
                    placeholder="Password"
                    defaultValue={this.state.userPassword}
                  />
                  <input
                    className="form-input"
                    id="userPasswordConfirm"
                    name="userPasswordConfirm"
                    type="password"
                    onBlur={this.handleInputChange}
                    onChange={this.handleInputChange}
                    onKeyDown={this.handleEnterKey}
                    placeholder="Confirm password"
                    defaultValue={this.state.userPasswordConfirm}
                  />
                  <h3 className="min-requirements">
                    <br />
                    {MIN_PASSWORD_REQUIREMENTS_TEXT}
                  </h3>
                </fieldset>
              )}
              {isNewUser && !passwordRadio && <fieldset className="user-form-spacer" />}

              <label className="form-input-label full-name" htmlFor="name">
                FULL NAME
                <input
                  className="form-input"
                  id="name"
                  maxLength="55"
                  name="name"
                  onBlur={this.handleInputChange}
                  onChange={this.handleInputChange}
                  onKeyDown={this.handleEnterKey}
                  value={this.state.name}
                />
              </label>
              <label className="form-input-label" htmlFor="location">
                LOCATION
                <input
                  className="form-input"
                  id="location"
                  maxLength="55"
                  name="location"
                  onBlur={this.handleInputChange}
                  onChange={this.handleInputChange}
                  onKeyDown={this.handleEnterKey}
                  value={this.state.location}
                />
              </label>
              <label className="form-input-label" htmlFor="role" style={{ marginBottom: 0 }}>
                ROLE {userIsSelf && '(Users may not change their own role)'}
              </label>
              <br />
              <UserRoleSelector
                roleList={availableRoles}
                onRoleChanged={this.handleInputChange}
                role={role}
                readOnly={userIsSelf}
              />
            </form>
          </div>
          <Modal isOpen={this.state.modalIsOpen} onRequestClose={() => this.closeModal()} style={customStyles}>
            <div className="total-modal">
              <div className="modal-header">
                <h2 className="modal-title">Email already registered</h2>
                <button type="button" className="anchor modal-closer" onClick={this.closeModal}>
                  &nbsp;
                </button>
              </div>
              <div className="modal-body">
                <span className="modal-copy">
                  {this.state.name} has been added to your account.
                  <br />
                  <br />
                  Since {this.state.name} was already registered as a Socialive user, they should log in using the
                  password they previously set.
                </span>
              </div>
              <div className="modal-footer">
                <ul className="modal-buttons">
                  <li>
                    <button type="button" className="btn" onClick={() => this.closeModal()}>
                      OK
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </Modal>
        </div>
      );
    }

    return <div />;
  }
}

const mapStateToProps = (state) => {
  const [, { enabled: isUserRolesEnabled }] = selectFeature(ACCOUNT_FEATURE.USER_ROLES)(state);
  return {
    isUserRolesEnabled,
    availableRoles: selectAvailableUserRoles(state),
    rolesLicense: selectRolesLicensingData(state),
  };
};

const mapDispatchToProps = { getRolesLicensing };

User.propTypes = {
  accountId: PropTypes.string.isRequired,
  APIBaseURL: PropTypes.string.isRequired,
  history: routerHistoryShape.isRequired,
  location: routerLocationShape.isRequired,
  match: routerMatchShape.isRequired,
  reAuth: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  isUserRolesEnabled: PropTypes.bool.isRequired,
  availableRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  getRolesLicensing: PropTypes.func.isRequired,
  rolesLicense: PropTypes.shape(
    Object.values(USER_ROLE).reduce(
      (result, role) => ({ ...result, [role]: { limit: PropTypes.number, current: PropTypes.number } }),
      {}
    )
  ),
};

User.defaultProps = {
  rolesLicense: undefined,
};

export default connect(mapStateToProps, mapDispatchToProps)(User);
